import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  MAX_DELIVERY_DAYS,
  describeDueAfter,
  type DeliveryMilestone,
  type DeliveryTask,
} from '@t3/shared';

// The delivery plan, editable: what the signed SOW becomes.
//
// Each milestone is due a number of days after kickoff (the day the
// client signs), and lists its tasks one per line - the same way plan
// inclusions and lists are typed everywhere else here. On acceptance the
// milestones are dated and the tasks become the technician's checklist
// and the client's progress bar.
//
// THE CLIENT READS IT. It renders on the quote as the Delivery plan and is
// frozen into what they sign; after signing they watch these same tasks
// being ticked off on their project page.

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

const newId = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** One task per non-blank line, keeping the ids of lines that did not change. */
function linesToTasks(text: string, previous: DeliveryTask[]): DeliveryTask[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((title, i) => {
      const same = previous[i];
      return same && same.title === title ? same : { id: newId('task'), title };
    });
}

export function DeliveryPlanEditor({
  plan,
  readOnly = false,
  onChange,
}: {
  plan: DeliveryMilestone[];
  readOnly?: boolean;
  onChange: (plan: DeliveryMilestone[]) => void;
}): React.ReactElement {
  const set = (index: number, patch: Partial<DeliveryMilestone>): void =>
    onChange(plan.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const move = (from: number, to: number): void => {
    if (to < 0 || to >= plan.length) return;
    const next = [...plan];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    onChange(next);
  };
  const totalTasks = plan.reduce((n, m) => n + m.tasks.length, 0);
  const lastDay = plan.length ? Math.max(...plan.map((m) => m.dueAfterDays)) : null;

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">
          Delivery plan{' '}
          <span className="font-normal text-[var(--color-muted)]">(the client sees this)</span>
        </h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() =>
              onChange([
                ...plan,
                {
                  id: newId('ms'),
                  title: '',
                  dueAfterDays: lastDay === null ? 0 : lastDay + 7,
                  tasks: [],
                },
              ])
            }
            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add milestone
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        Shown on the quote as the timeline the client signs. When they sign, it becomes the
        project: each milestone dated from that day, its tasks the technician&apos;s checklist and
        the client&apos;s progress bar.
        {plan.length > 0 &&
          ` ${plan.length} milestone${plan.length === 1 ? '' : 's'}, ${totalTasks} task${
            totalTasks === 1 ? '' : 's'
          }, due ${lastDay} day${lastDay === 1 ? '' : 's'} after signing.`}
      </p>

      {plan.length === 0 ? (
        <p className="rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-white px-4 py-5 text-center text-sm text-[var(--color-muted)]">
          No plan yet. Without one the project is created with no milestones or tasks, and they are
          added on the project.
        </p>
      ) : (
        <div className="space-y-3">
          {plan.map((milestone, index) => (
            <MilestoneRow
              key={milestone.id}
              index={index}
              milestone={milestone}
              last={index === plan.length - 1}
              readOnly={readOnly}
              onChange={(patch) => set(index, patch)}
              onMove={(to) => move(index, to)}
              onRemove={() => onChange(plan.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MilestoneRow({
  index,
  milestone,
  last,
  readOnly,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  milestone: DeliveryMilestone;
  last: boolean;
  readOnly: boolean;
  onChange: (patch: Partial<DeliveryMilestone>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}): React.ReactElement {
  // The textarea keeps its own text, blank lines and all, so pressing
  // Enter to start the next task works. Only the parsed tasks go up.
  const [text, setText] = useState(() => milestone.tasks.map((t) => t.title).join('\n'));
  const n = index + 1;
  return (
    <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_11rem_auto]">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">Milestone</span>
          <input
            value={milestone.title}
            disabled={readOnly}
            onChange={(e) => onChange({ title: e.target.value })}
            className={inputClass}
            placeholder="e.g. Firewall cut over"
            aria-label={`Milestone ${n} name`}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
            Due, days after signing
          </span>
          <input
            type="number"
            min={0}
            max={MAX_DELIVERY_DAYS}
            step={1}
            value={milestone.dueAfterDays}
            disabled={readOnly}
            onChange={(e) =>
              onChange({ dueAfterDays: Math.max(0, Math.floor(Number(e.target.value) || 0)) })
            }
            className={inputClass}
            aria-label={`Milestone ${n} due days after signing`}
          />
          {/* What the client will read for this number. */}
          <span className="mt-1 block text-xs text-[var(--color-muted)]">
            Client reads: {describeDueAfter(milestone.dueAfterDays)}
          </span>
        </label>
        {!readOnly && (
          <div className="flex items-end gap-1">
            <button
              type="button"
              onClick={() => onMove(index - 1)}
              disabled={index === 0}
              className="rounded p-2 text-[var(--color-muted)] hover:bg-[var(--color-surface)] disabled:opacity-30"
              aria-label={`Move milestone ${n} up`}
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => onMove(index + 1)}
              disabled={last}
              className="rounded p-2 text-[var(--color-muted)] hover:bg-[var(--color-surface)] disabled:opacity-30"
              aria-label={`Move milestone ${n} down`}
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="rounded p-2 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
              aria-label={`Remove milestone ${n}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
          Tasks - one per line
        </span>
        <textarea
          rows={Math.min(10, Math.max(3, text.split('\n').length + 1))}
          value={text}
          disabled={readOnly}
          onChange={(e) => {
            setText(e.target.value);
            onChange({ tasks: linesToTasks(e.target.value, milestone.tasks) });
          }}
          className={inputClass}
          placeholder={'Kickoff call with the client\nConfirm admin access'}
          aria-label={`Milestone ${n} tasks`}
        />
      </label>
    </div>
  );
}
